import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Wordmark } from "@/components/landing/shared";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", company_name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({
        full_name: form.full_name,
        email: form.email,
        password: form.password,
        company_name: form.company_name || undefined,
      });
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="register-page">
      <div className="w-full max-w-md">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-8">
            <div className="mb-6">
              <Wordmark />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">Crea il tuo account</h1>
            <p className="text-sm text-slate-600 mb-6">
              Registrati e crea la tua azienda per iniziare il percorso ESG.
            </p>

            {error && (
              <div role="alert" data-testid="register-error" className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
                {error}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-name">Nome e cognome *</Label>
                <Input id="reg-name" required minLength={2} autoComplete="name"
                  data-testid="register-name-input" value={form.full_name} onChange={set("full_name")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-email">Email *</Label>
                <Input id="reg-email" type="email" required autoComplete="email"
                  data-testid="register-email-input" value={form.email} onChange={set("email")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-password">Password * (minimo 8 caratteri)</Label>
                <Input id="reg-password" type="password" required minLength={8} autoComplete="new-password"
                  data-testid="register-password-input" value={form.password} onChange={set("password")} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reg-company">Denominazione azienda</Label>
                <Input id="reg-company" placeholder="La tua impresa (creata al primo accesso)"
                  data-testid="register-company-input" value={form.company_name} onChange={set("company_name")} />
                <p className="text-xs text-slate-500">Se la lasci vuota, potrai creare l'azienda dopo l'accesso.</p>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-green-700 hover:bg-green-800" data-testid="register-submit-button">
                {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> Registrazione…</>) : "Registrati"}
              </Button>
            </form>

            <p className="text-sm text-slate-600 mt-6 text-center">
              Hai già un account?{" "}
              <Link to="/login" className="text-green-700 font-semibold hover:underline" data-testid="go-to-login-link">
                Accedi
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
